<template>
  <div id="AppContainer">
    <!-- Kill switch: while the `maintenance-page` flag is on, no page-level code
         mounts — no router view, no toasts, no modals. Bootstrap in `main.ts` keeps
         running (it is what re-evaluates the flag), so this hides the app, it does
         not stop it -->
    <PageMaintenance v-if="isMaintenanceMode" />
    <template v-else>
      <RouterView v-if="isAppLoaded" />
      <UIPageLoader v-else class="AppContainer__loader" />
      <UIToast />
      <ModalPersonalisation
        v-if="personalisationModal.isOpen"
        :isOpen="true"
        @close="personalisationModal.close"
      />
      <UIAlert />
    </template>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { usePersonalisationModal } from '@/composables/usePersonalisationModal'
import { isMaintenanceMode } from '@/lib/maintenanceMode'

import { useAppStore } from './store/app'
import ModalPersonalisation from './views/components/modals/ModalPersonalisation.vue'
import UIAlert from './views/components/ui/alerts/UIAlert.vue'
import UIPageLoader from './views/components/ui/loaders/UIPageLoader.vue'
import UIToast from './views/components/ui/toasts/UIToast.vue'
// Imported eagerly, unlike every route: a lazy chunk that 404s would render nothing
// at all, and the deploy that purges old chunks is exactly when this screen is up.
// It costs ~nothing — the background artwork is a `background-image` URL the browser
// only fetches once the screen actually renders.
import PageMaintenance from './views/pages/Maintenance/Index.vue'
import '@foundever/ui-lib/style.css'

const { isAppLoaded } = storeToRefs(useAppStore())
const personalisationModal = usePersonalisationModal()
</script>

<style lang="scss">
@use '@foundever/ui-lib/mixins.scss' as *;

p {
  color: $color-neutral-170;
}
// Override default
:-webkit-any(article, aside, nav, section) h1 {
  font-size: 2em;
}
p {
  margin: 0;
}
* {
  --sidebar-width: 14.5625rem;

  padding: 0;
  margin: 0;
  background-repeat: no-repeat;
  box-sizing: inherit;

  &:before,
  &:after {
    background-repeat: no-repeat;
    box-sizing: inherit;
  }
}

html {
  line-height: 1.5;
  font-family:
    Noto Sans,
    sans-serif;
  font-size: 1rem;
  overflow-x: hidden;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%;
  word-break: normal;
  -moz-tab-size: 4;
  tab-size: 4;
  box-sizing: border-box;
}

button {
  background-color: transparent;
  border-style: none;
  cursor: pointer;
}

button,
input,
optgroup,
select,
textarea {
  font: inherit;
  appearance: none;
}

body {
  margin: 0;
}

#AppContainer {
  display: flex;
  min-height: 100vh;
  color: $color-neutral-170;
  background-color: $color-neutral-5;

  .AppContainer__loader {
    width: 100%;
    height: 100vh;
  }
}
</style>
